// Global Instructions Rule Applied!
//
// The evidence behind one highlight item: the interactions, signals, rounds,
// transactions, KPI values and states it cites, each with its excerpt and a
// record link where the app has a form for it. Chrome copies the
// CustomizeReportDrawer header (the house antd Drawer treatment).
import React from 'react'
import { Button as AntButton, Drawer, Tag, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { portalGradients } from '../../../portal-configs/colors.js'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { HighlightItemRecord } from '../../../state_manager/data_models/period_highlights_model.ts'

const EVIDENCE_LABEL: Record<string, string> = {
  interaction: 'Interaction',
  signal: 'Signal',
  round: 'Round',
  asset_transaction: 'Transaction',
  kpi_value: 'KPI value',
  company_state: 'State'
}

// Which record form each evidence link opens (accounts → the company form).
const LINK_MODULE: Record<string, string> = {
  accounts: 'companies',
  rounds: 'rounds',
  interactions: 'interactions'
}

interface EvidenceDrawerProps {
  item: HighlightItemRecord | null
  onClose: () => void
}

export const EvidenceDrawer: React.FC<EvidenceDrawerProps> = ({ item, onClose }) => {
  const { openFormInTab } = useTabManagerContext()
  const evidence = item?.prog__evidence ?? []
  const metrics = item?.highlight_items__metrics ?? {}
  const metricEntries = Object.entries(metrics).filter(([, v]) => v != null && typeof v !== 'object')

  return (
    <Drawer
      title={
        <div className='flex w-full min-w-0 items-center justify-between gap-2'>
          <span className='min-w-0 flex-1 truncate text-base font-semibold leading-snug text-white'>Evidence</span>
          <Tooltip title='Close'>
            <AntButton
              type='default'
              size='small'
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={onClose}
              aria-label='Close'
              className='flex-shrink-0 bg-white/20 !border-white/40 !text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
              style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
            />
          </Tooltip>
        </div>
      }
      placement='right'
      width={480}
      open={item != null}
      onClose={onClose}
      closable={false}
      destroyOnHidden
      classNames={{
        header:
          'shadow-lg !border-b-0 !h-10 !min-h-10 !max-h-10 !p-0 !px-4 flex items-center [&_.ant-drawer-header-title]:!m-0 [&_.ant-drawer-header-title]:!w-full [&_.ant-drawer-header-title]:!flex-1 [&_.ant-drawer-header-title]:!flex [&_.ant-drawer-header-title]:min-w-0 [&_.ant-drawer-header-title]:items-center'
      }}
      styles={{
        header: {
          background: portalGradients.navbar,
          height: 40, minHeight: 40, maxHeight: 40,
          padding: '0 16px', display: 'flex', alignItems: 'center',
          borderBottom: 'none', boxSizing: 'border-box'
        },
        body: { padding: '12px 16px 16px' }
      }}
    >
      {item && (
        <div data-component='EvidenceDrawer' className='flex flex-col gap-3 text-[13px]'>
          <div>
            <div className='text-xs uppercase tracking-wide text-gray-500 mb-1'>Item</div>
            <div className='font-semibold text-gray-800'>{item.highlight_items__headline}</div>
            {item.highlight_items__body && <div className='text-gray-700 mt-1'>{item.highlight_items__body}</div>}
            <div className='mt-1'>
              <Tag color={item.highlight_categories__polarity === 'positive' ? 'green' : item.highlight_categories__polarity === 'negative' ? 'red' : 'default'}>
                {item.highlight_categories__label}
              </Tag>
              {item.highlight_categories__deterministic && <Tag color='purple'>From the books</Tag>}
              {item.highlight_items__confidence != null && (
                <span className='text-gray-500 text-xs'>confidence {Math.round(item.highlight_items__confidence * 100)}%</span>
              )}
            </div>
          </div>

          {metricEntries.length > 0 && (
            <div>
              <div className='text-xs uppercase tracking-wide text-gray-500 mb-1'>Frozen metrics</div>
              <table className='text-xs w-full'>
                <tbody>
                  {metricEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td className='text-gray-500 pr-3 py-0.5 align-top whitespace-nowrap'>{key}</td>
                      <td className='text-gray-800 py-0.5 break-all'>{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <div className='text-xs uppercase tracking-wide text-gray-500 mb-1'>Cited evidence</div>
            {evidence.length === 0 && <div className='text-gray-500 italic'>No evidence recorded for this item.</div>}
            <ul className='list-none m-0 p-0 flex flex-col gap-2'>
              {evidence.map((e) => {
                const link = e.prog__link
                const module = link?.table ? LINK_MODULE[link.table] : undefined
                return (
                  <li key={e.highlight_item_evidence__id} className='border border-gray-200 rounded p-2'>
                    <div className='flex items-center gap-2'>
                      <Tag>{EVIDENCE_LABEL[e.highlight_item_evidence__evidence_type] ?? e.highlight_item_evidence__evidence_type}</Tag>
                      {e.prog__occurred_at && (
                        <span className='text-xs text-gray-500'>{dayjs(e.prog__occurred_at).format('MM/DD/YYYY')}</span>
                      )}
                      {module && link?.id ? (
                        <button
                          type='button'
                          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left ml-auto text-xs'
                          onClick={() => openFormInTab(module, link.id as string, e.prog__label || 'Record')}
                        >
                          Open
                        </button>
                      ) : null}
                    </div>
                    {e.prog__label && <div className='text-gray-800 mt-1'>{e.prog__label}</div>}
                    {e.highlight_item_evidence__excerpt && (
                      <blockquote className='mt-1 mb-0 pl-2 border-l-2 border-gray-300 text-gray-600 italic'>
                        {e.highlight_item_evidence__excerpt}
                      </blockquote>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </Drawer>
  )
}

export default EvidenceDrawer
